import React from "react"
import { useTranslation } from "react-i18next"
import { SortAsc, SortDesc } from "lucide-react"

import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"

interface SortToggleProps {
  value?: "asc" | "desc"
  compact?: boolean
  /** Hide the visible label and render just the icon button. */
  iconOnly?: boolean
  onChange: (next: "asc" | "desc") => void
}

const SortToggle: React.FC<SortToggleProps> = ({
  value = "desc",
  compact = false,
  iconOnly = false,
  onChange,
}) => {
  const { t } = useTranslation()
  const isDesc = value === "desc"
  const Icon = isDesc ? SortDesc : SortAsc

  return (
    <div
      className={
        iconOnly
          ? ""
          : compact
            ? "grid justify-items-end gap-1.5"
            : "mb-4 flex items-center ml-0 shrink-0"
      }
    >
      <label
        className={
          iconOnly || !compact
            ? "sr-only"
            : "text-xs font-medium text-muted-foreground"
        }
        htmlFor="history-sort-toggle"
      >
        {t("historyList.sort.label")}
      </label>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              id="history-sort-toggle"
              variant="outline"
              className={
                iconOnly
                  ? "h-8 w-8 p-0"
                  : compact
                    ? "h-9 w-9 p-0"
                    : "h-10 w-10 p-0"
              }
              aria-label={t("historyList.sort.label")}
              onClick={() => onChange(isDesc ? "asc" : "desc")}
            >
              <Icon
                className={
                  iconOnly ? "h-3.5 w-3.5" : compact ? "h-4 w-4" : "h-5 w-5"
                }
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isDesc ? t("historyList.sort.desc") : t("historyList.sort.asc")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

export default React.memo(SortToggle)
